import React from 'react';
import { bool, string } from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { hasListingAccess, hasSearchAccess, getPaymentURL } from '../../util/paymentHelpers';
import { PrimaryButton, SecondaryButton } from '../Button/Button';
import { IconLock } from '../../components';

import css from './PaymentGate.module.css';

const PaymentGate = ({ currentUser, type, children }) => {
  const history = useHistory();
  const location = useLocation();
  
  // If user is not logged in, redirect to signup
  if (!currentUser) {
    history.push('/signup', { from: `${location.pathname}${location.search}${location.hash}` });
    return null;
  }
  
  // Check if user has access
  const hasAccess = type === 'listing' 
    ? hasListingAccess(currentUser) 
    : hasSearchAccess(currentUser);

  if (hasAccess) {
    return children;
  }

  const config = {
    listing: {
      title: 'List Your Space',
      description: 'Subscribe to list your space on the marketplace',
      primaryPrice: '$99.00',
      priceInterval: 'per year',
      secondaryPrice: null,
      priceDetail: 'Billed annually • Cancel anytime',
      benefits: [
        'Create unlimited space listings',
        'Manage your listings dashboard',
        'Connect with beauty professionals',
        'Set your own pricing and availability',
      ],
    },
    search: {
      title: 'Search for Spaces',
      description: 'Subscribe to search and book spaces',
      primaryPrice: '$55.00',
      priceInterval: 'per year',
      secondaryPrice: '$5.95/month',
      priceDetail: 'Choose your plan • Cancel anytime',
      benefits: [
        'Browse all available spaces',
        'Advanced search filters',
        'Direct messaging with space owners',
        'Book spaces instantly',
      ],
    },
  };

  const content = config[type];

  const handlePayment = () => {
    history.push(getPaymentURL(type));
  };

  const handleGoBack = () => {
    history.push('/');
  };

  return (
    <div className={css.root}>
      <div className={css.content}>
        <div className={css.header}>
          <h1 className={css.title}>{content.title}</h1>
          <p className={css.description}>{content.description}</p>
        </div>

        <div className={css.priceCard}>
          <div className={css.priceMain}>
            <span className={css.priceAmount}>{content.primaryPrice}</span>
            <span className={css.priceInterval}>{content.priceInterval}</span>
          </div>
          {content.secondaryPrice && (
            <div className={css.priceSecondary}>
              or {content.secondaryPrice}
            </div>
          )}
          <div className={css.priceLabel}>{content.priceDetail}</div>
        </div>

        <div className={css.benefits}>
          <h3 className={css.benefitsTitle}>What you'll get:</h3>
          <ul className={css.benefitsList}>
            {content.benefits.map((benefit, index) => (
              <li key={index} className={css.benefitItem}>
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        <div className={css.actions}>
          <PrimaryButton className={css.payButton} onClick={handlePayment}>
            Continue to Payment
          </PrimaryButton>
          <SecondaryButton className={css.backButton} onClick={handleGoBack}>
            Go Back
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
};

PaymentGate.propTypes = {
  currentUser: propTypes.currentUser,
  type: string.isRequired,
  children: propTypes.children,
};

export default PaymentGate;

